import React from "react";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import { Layout, Table, Button, Space, Tag, Modal, message, Tabs } from "antd";
import { DeleteOutlined, CheckCircleOutlined, ExclamationCircleOutlined, UndoOutlined } from "@ant-design/icons";

import PageLayout from "../../components/common/Layouts/PageLayout";
import { publishEvent, deleteEvent, restoreEvent, permanentDeleteEvent, fetchAdminEvents } from "../../redux/eventSlice";

const mapApiEventToUIEvent = (apiEvent) => {
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "";
    try {
      return dayjs(dateTimeString).format("DD MMM YYYY | hh:mm A");
    } catch {
      return dateTimeString;
    }
  };

  const getStringValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === 'object') return value.name || value.event_type_name || value.venue_name || fallback;
    if (typeof value === 'string') return value;
    return fallback;
  };

  return {
    ...apiEvent, // Keep original fields for actions
    key: apiEvent.id, // Ensure key is set
    name: apiEvent.event_name || "Untitled Event",
    slug: apiEvent.slug || "—",
    venue: getStringValue(apiEvent.venue || apiEvent.event_venue, ""),
    category: getStringValue(apiEvent.category, "—"),
    startDate: formatDateTime(apiEvent.event_start_date_time),
    endDate: formatDateTime(apiEvent.event_end_date_time),
    isPublished: apiEvent.is_event_published,
    isDeleted: apiEvent.is_delete,
  };
};

const { Content } = Layout;

export default function AllEvents() {
  const dispatch = useDispatch();
  const [modal, contextHolder] = Modal.useModal();

  React.useEffect(() => {
    dispatch(fetchAdminEvents());
  }, [dispatch]);

  // Get events from Redux store and map them
  const rawEvents = useSelector((state) => state.event.events);
  
  // Safely handle events data whether it's an array or { data: [] }
  const eventsList = Array.isArray(rawEvents) 
    ? rawEvents 
    : (rawEvents?.data || []);

  const allEvents = eventsList.map(mapApiEventToUIEvent);

  // Filter and sort events for each section (newest first)
  const sortEvents = (events) => [...events].sort((a, b) => {
    return new Date(b.created_at || b.id) - new Date(a.created_at || a.id);
  });

  const activeEvents = sortEvents(allEvents.filter((e) => !e.isDeleted));
  const publishedEvents = sortEvents(allEvents.filter((e) => e.isPublished && !e.isDeleted));
  const deletedEvents = sortEvents(allEvents.filter((e) => e.isDeleted));

  // Handle publish action
  const handlePublish = (record) => {
    dispatch(publishEvent(record));
    // Optimistic message or wait for thunk result?
    // Thunk is async. Better to wait or rely on state change.
    // For now showing message immediately to be responsive, but "Request sent" might be better.
    // However, existing code showed success immediately.
    // Let's keep it simple for now as per user request flow "click -> flag true".
    // Ideally we should wait for promise resolution:
    // dispatch(publishEvent(record)).unwrap().then(() => message.success(...)).catch(...)
    // But since I didn't verify unwrap support (createAsyncThunk provides it, manual thunk returns promise of dispatch result),
    // let's just dispatch and show generic message or rely on global error handler.
    // Actually, let's keep the message logic simple but maybe update text.
    message.info(`Processing publish request for "${record.name}"...`);
  };

  // Handle soft delete action
  const handleDelete = (record) => {
    modal.confirm({
      title: "Are you sure you want to delete this event?",
      icon: <ExclamationCircleOutlined />,
      content: `Event: ${record.name}`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        dispatch(deleteEvent(record.key));
        message.info(`Deleting "${record.name}"...`);
      },
    });
  };

  // Handle restore action
  const handleRestore = (record) => {
    dispatch(restoreEvent(record.key));
    message.success(`"${record.name}" has been restored`);
  };

  // Handle permanent delete action
  const handlePermanentDelete = (record) => {
    modal.confirm({
      title: "Permanently delete this event?",
      icon: <ExclamationCircleOutlined />,
      content: `This action cannot be undone. Event: ${record.name}`,
      okText: "Yes, Delete Permanently",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        dispatch(permanentDeleteEvent(record.key));
        message.success(`"${record.name}" has been permanently deleted`);
      },
    });
  };

  // Base columns for all tables
  const baseColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <span style={{ fontWeight: 500, color: "#000738" }}>{text}</span>,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (text) => (
        <Tag color="blue" style={{ borderRadius: 30 }}>
          {text}
        </Tag>
      ),
    },
    {
      title: "Slug",
      dataIndex: "slug",
      key: "slug",
      render: (text) => (
        <code style={{ color: "#555", fontSize: 13 }}>{text}</code>
      ),
    },
    {
      title: "Venue",
      dataIndex: "venue",
      key: "venue",
      render: (text) => <span style={{ color: "#555" }}>{text}</span>,
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      render: (text) => <span style={{ color: "#61A0FF" }}>{text}</span>,
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      render: (text) => <span style={{ color: "#61A0FF" }}>{text}</span>,
    },
  ];

  // Columns for All Events section
  const allEventsColumns = [
    ...baseColumns,
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Tag color={record.isPublished ? "green" : "orange"}>
          {record.isPublished ? "Published" : "Draft"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type={record.isPublished ? "default" : "primary"}
            icon={<CheckCircleOutlined />}
            onClick={() => handlePublish(record)}
            style={{
              backgroundColor: record.isPublished ? "#fff" : "#52c41a",
              borderColor: record.isPublished ? "#d9d9d9" : "#52c41a",
              color: record.isPublished ? "#000" : "#fff",
            }}
          >
            {record.isPublished ? "Unpublish" : "Publish"}
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  // Columns for Published section
  const publishedColumns = [
    ...baseColumns,
    {
      title: "Status",
      key: "status",
      render: () => <Tag color="green">Published</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Button
            icon={<CheckCircleOutlined />}
            onClick={() => handlePublish(record)}
          >
            Unpublish
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  // Columns for Deleted section
  const deletedColumns = [
    ...baseColumns,
    {
      title: "Status",
      key: "status",
      render: () => <Tag color="red">Deleted</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<UndoOutlined />}
            onClick={() => handleRestore(record)}
            style={{ backgroundColor: "#1890ff", borderColor: "#1890ff" }}
          >
            Restore
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handlePermanentDelete(record)}
          >
            Delete Forever
          </Button>
        </Space>
      ),
    },
  ];

  // Card style
  const cardStyle = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "24px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    marginBottom: "20px",
  };

  const titleStyle = {
    marginBottom: "20px",
    color: "#000738",
    fontSize: "20px",
    fontWeight: 600,
  };

  // Tab items
  const tabItems = [
    {
      key: "all",
      label: `All Events (${activeEvents.length})`,
      children: (
        <div style={cardStyle}>
          <h2 style={titleStyle}>All Events</h2>
          <Table
            columns={allEventsColumns}
            dataSource={activeEvents}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} events`,
            }}
            bordered={false}
            style={{ overflowX: "auto" }}
          />
        </div>
      ),
    },
    {
      key: "published",
      label: `Published (${publishedEvents.length})`,
      children: (
        <div style={cardStyle}>
          <h2 style={titleStyle}>Published Events</h2>
          <Table
            columns={publishedColumns}
            dataSource={publishedEvents}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} events`,
            }}
            bordered={false}
            style={{ overflowX: "auto" }}
          />
        </div>
      ),
    },
    {
      key: "deleted",
      label: `Deleted (${deletedEvents.length})`,
      children: (
        <div style={cardStyle}>
          <h2 style={titleStyle}>Deleted Events</h2>
          <Table
            columns={deletedColumns}
            dataSource={deletedEvents}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} events`,
            }}
            bordered={false}
            style={{ overflowX: "auto" }}
          />
        </div>
      ),
    },
  ];

  return (
    <PageLayout title="My Events">
      {contextHolder}
      <Content className="contentWrap">
        <div className="relative" style={{ padding: "20px" }}>
          <Tabs
            defaultActiveKey="all"
            items={tabItems}
            type="card"
            style={{ marginTop: "-10px" }}
          />
        </div>
      </Content>
    </PageLayout>
  );
}